import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2, Download, Bell, Shield, AlertTriangle, MailCheck, Crown, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import TwoFASection from "@/components/TwoFASection";
import LoginHistorySection from "@/components/LoginHistorySection";
import NotificationPreferences from "@/components/NotificationPreferences";
import DigestModeSection from "@/components/DigestModeSection";
import PasswordInput from "@/components/PasswordInput";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(user?.email_notifications !== false);
  const [subProvider, setSubProvider] = useState(null);
  // Change password state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdNew !== pwdConfirm) { toast.error("Les deux nouveaux mots de passe ne correspondent pas"); return; }
    if (pwdNew.length < 8) { toast.error("8 caractères minimum"); return; }
    if (pwdNew === pwdCurrent) { toast.error("Le nouveau mot de passe doit être différent de l'ancien"); return; }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdCurrent, new_password: pwdNew });
      toast.success("Mot de passe mis à jour");
      setPwdOpen(false);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setPwdBusy(false);
    }
  };

  useEffect(() => {
    if (!user?.is_premium) { setSubProvider(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/subscription");
        setSubProvider(data?.provider || null);
      } catch { setSubProvider(null); }
    })();
  }, [user?.is_premium]);

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "student-hub-export.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Tes données ont été exportées");
    } catch (e) {
      toast.error("Export impossible");
    }
  };

  const toggleEmail = async (next) => {
    setEmailOptIn(next);
    try {
      await api.patch("/me/preferences", { email_notifications: next });
      toast.success(next ? "Notifications activées" : "Notifications désactivées");
    } catch {
      setEmailOptIn(!next);
      toast.error("Mise à jour impossible");
    }
  };

  const deleteAccount = async () => {
    if (!pwd) { toast.error("Mot de passe requis"); return; }
    setBusy(true);
    try {
      await api.delete("/me", { data: { password: pwd } });
      toast.success("Compte supprimé. À bientôt.");
      await logout();
      navigate("/login");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusy(false);
      setPwd("");
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Paramètres</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Mon compte</h1>
        <p className="text-slate-600 mt-2">Gère tes préférences, exporte ou supprime tes données conformément au RGPD.</p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="font-display text-lg font-semibold text-slate-900">Informations</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
          <div><p className="text-slate-500">Nom</p><p className="text-slate-900 font-medium">{user?.name}</p></div>
          <div>
            <p className="text-slate-500">Email</p>
            <p className="text-slate-900 font-medium flex items-center gap-2">
              {user?.email}
              {user?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="email-verified-badge">
                  <MailCheck size={11} /> Vérifié
                </span>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      await api.post("/auth/resend-verification");
                      toast.success("Email envoyé. Vérifie ta boîte mail (et spam).");
                    } catch {
                      toast.error("Trop de demandes. Réessaie plus tard.");
                    }
                  }}
                  className="text-xs text-amber-700 underline hover:text-amber-900"
                  data-testid="settings-resend-verify"
                >
                  Confirmer
                </button>
              )}
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${user?.is_premium ? "bg-yellow-50 text-yellow-700" : "bg-slate-100 text-slate-500"}`}>
            <Crown size={18} />
          </div>
          <div>
            <p className="font-medium text-slate-900">Abonnement Premium</p>
            {user?.is_premium ? (
              <p className="text-sm text-slate-500">Tu es Premium {user?.premium_until ? `· valide jusqu'au ${new Date(user.premium_until).toLocaleDateString("fr-FR")}` : ""}{subProvider === "paypal" ? " · payé via PayPal" : subProvider === "stripe" ? " · payé par carte" : ""}.</p>
            ) : (
              <p className="text-sm text-slate-500">Tu n'es pas encore Premium. Passe Premium pour débloquer les modèles, l'IA illimitée et les bons plans exclusifs.</p>
            )}
          </div>
        </div>
        {user?.is_premium ? (
          <div className="flex flex-col gap-2 shrink-0">
            {subProvider === "stripe" && (
              <Button
                variant="outline"
                className="shrink-0"
                data-testid="manage-subscription-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/payments/portal", { return_url: window.location.href });
                    window.location.href = data.url;
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Portail indisponible");
                  }
                }}
              >
                <ExternalLink size={14} className="mr-1.5" /> Gérer (Stripe)
              </Button>
            )}
            {subProvider === "paypal" && (
              <Button
                variant="outline"
                className="shrink-0 text-red-600 hover:text-red-700 hover:border-red-200"
                data-testid="paypal-cancel-btn"
                onClick={async () => {
                  if (!window.confirm("Annuler ton abonnement PayPal ? Tu garderas l'accès Premium jusqu'à la fin de la période payée.")) return;
                  try {
                    await api.post("/payments/paypal/cancel");
                    toast.success("Abonnement PayPal annulé.");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Annulation impossible");
                  }
                }}
              >
                Annuler PayPal
              </Button>
            )}
            {!subProvider && (
              <p className="text-xs text-slate-400 shrink-0">Code promo / essai</p>
            )}
          </div>
        ) : (
          <Button asChild className="shrink-0 bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="upgrade-cta">
            <a href="/premium"><Crown size={14} className="mr-1.5" /> Passer Premium</a>
          </Button>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-between gap-4" data-testid="email-notif-card">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Bell size={18} />
          </div>
          <div>
            <p className="font-medium text-slate-900">Notifications hebdo « Ma semaine »</p>
            <p className="text-sm text-slate-500">Email dominical avec progrès, parrainages et 1 rappel personnalisé.</p>
          </div>
        </div>
        <Switch checked={emailOptIn} onCheckedChange={toggleEmail} data-testid="email-notif-toggle" />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-between gap-4" data-testid="change-password-card">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
            <KeyRound size={18} />
          </div>
          <div>
            <p className="font-medium text-slate-900">Mot de passe</p>
            <p className="text-sm text-slate-500">Modifie ton mot de passe régulièrement pour sécuriser ton compte.</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setPwdOpen(true)} data-testid="change-password-btn">
          Changer
        </Button>
      </section>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Changer mon mot de passe</DialogTitle>
            <DialogDescription>Tape ton mot de passe actuel puis ton nouveau (8 caractères minimum).</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label htmlFor="pwd-current" className="text-xs">Mot de passe actuel</Label>
              <PasswordInput id="pwd-current" autoComplete="current-password" required
                value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} className="mt-1.5"
                data-testid="pwd-current-input" />
            </div>
            <div>
              <Label htmlFor="pwd-new" className="text-xs">Nouveau mot de passe</Label>
              <PasswordInput id="pwd-new" autoComplete="new-password" required minLength={8}
                value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} className="mt-1.5"
                data-testid="pwd-new-input" />
            </div>
            <div>
              <Label htmlFor="pwd-confirm" className="text-xs">Confirme le nouveau mot de passe</Label>
              <PasswordInput id="pwd-confirm" autoComplete="new-password" required minLength={8}
                value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} className="mt-1.5"
                data-testid="pwd-confirm-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwdOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={pwdBusy} data-testid="pwd-submit-btn">
                {pwdBusy ? "..." : "Mettre à jour"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <TwoFASection />

      <NotificationPreferences />

      <DigestModeSection />

      <LoginHistorySection />

      <section className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Shield size={18} />
          </div>
          <div>
            <p className="font-medium text-slate-900">Exporter mes données (RGPD)</p>
            <p className="text-sm text-slate-500">Récupère un fichier JSON avec ton profil, démarches, documents, posts et paiements.</p>
          </div>
        </div>
        <Button variant="outline" onClick={exportData} className="shrink-0" data-testid="export-data-btn">
          <Download size={14} className="mr-1.5" /> Exporter
        </Button>
      </section>

      <section className="rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="delete-account-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-red-900">Supprimer mon compte</p>
            <p className="text-sm text-red-800/80 mt-1">
              Action définitive. Tes documents, démarches, posts et préférences seront effacés.
              Conformément au RGPD, certaines données légales (factures) sont conservées 10 ans en archive anonymisée.
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive" className="mt-4" data-testid="open-delete-dialog">
                  <Trash2 size={14} className="mr-1.5" /> Supprimer définitivement
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="delete-confirm-dialog">
                <DialogHeader>
                  <DialogTitle>Confirmer la suppression</DialogTitle>
                  <DialogDescription>Saisis ton mot de passe pour confirmer. Cette action est irréversible.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Label htmlFor="dpwd">Mot de passe</Label>
                  <PasswordInput id="dpwd" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="delete-password-input" />
                </div>
                <DialogFooter>
                  <Button variant="destructive" disabled={busy} onClick={deleteAccount} data-testid="confirm-delete-btn">
                    {busy ? "Suppression..." : "Je supprime mon compte"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
    </div>
  );
}
